import { antdFormRootSelector } from '@/utils/antdFormDom';

import type { AntdFieldError } from '@/utils/validation/zodToAntdFieldErrors';

type FieldErrorVisibility = 'visible' | 'revealable' | 'inapplicable';

type ClassifyForm = {
  getFieldInstance?: (name: string | (string | number)[]) => unknown;
  scrollToField?: (
    name: string | (string | number)[],
    options?: { behavior?: ScrollBehavior; block?: ScrollLogicalPosition },
  ) => void;
};

/** Closest ant Form.Item root for a control or wrapper node. */
function findFormItemElement(node: unknown): HTMLElement | null {
  if (node == null || typeof node !== 'object') {
    return null;
  }
  const candidate =
    'nativeElement' in node &&
    (node as { nativeElement?: unknown }).nativeElement instanceof HTMLElement
      ? (node as { nativeElement: HTMLElement }).nativeElement
      : node instanceof HTMLElement
        ? node
        : 'input' in node &&
            (node as { input?: unknown }).input instanceof HTMLElement
          ? (node as { input: HTMLElement }).input
          : null;
  if (!candidate) {
    return null;
  }
  return candidate.closest('.ant-form-item');
}

function isFormItemHidden(item: HTMLElement): boolean {
  if (item.classList.contains('ant-form-item-hidden')) {
    return true;
  }
  if (item.getAttribute('hidden') != null) {
    return true;
  }
  const style = window.getComputedStyle?.(item);
  if (style && (style.display === 'none' || style.visibility === 'hidden')) {
    return true;
  }
  return false;
}

function isInInactiveTabPane(item: HTMLElement): boolean {
  const hiddenPane = item.closest(
    '.ant-tabs-tabpane-hidden, .ant-tabs-tabpane[aria-hidden="true"]',
  );
  return hiddenPane != null;
}

/** Activate the Ant Design tab that contains the error field, if any. */
function revealTabForFormItem(item: HTMLElement): boolean {
  const pane = item.closest('.ant-tabs-tabpane');
  if (!(pane instanceof HTMLElement)) {
    return false;
  }
  const paneId = pane.getAttribute('id');
  if (!paneId) {
    return false;
  }
  const tablist = pane
    .closest('.ant-tabs')
    ?.querySelector('.ant-tabs-nav, [role="tablist"]');
  if (!tablist) {
    return false;
  }
  const tab =
    tablist.querySelector(`[aria-controls="${paneId}"]`) ??
    tablist.querySelector(`#${CSS.escape(paneId.replace(/-panel-/, '-tab-'))}`);
  if (tab instanceof HTMLElement) {
    tab.click();
    return true;
  }
  return false;
}

/**
 * Classify whether a Zod/antd field error can be shown to the user.
 * When `getFieldInstance` is missing (unit tests / headless), treat as visible
 * so existing schema failures still block submit.
 */
export function classifyFieldErrorVisibility(
  form: ClassifyForm,
  name: string | (string | number)[],
): FieldErrorVisibility {
  if (name === '_form' || (Array.isArray(name) && name[0] === '_form')) {
    return 'visible';
  }

  if (typeof form.getFieldInstance !== 'function') {
    return 'visible';
  }

  const instance = form.getFieldInstance(name);
  if (instance == null) {
    return 'inapplicable';
  }

  const item = findFormItemElement(instance);
  if (!item) {
    // Control exists but we cannot resolve the Form.Item — keep blocking.
    return 'visible';
  }

  if (isFormItemHidden(item)) {
    return 'inapplicable';
  }

  if (isInInactiveTabPane(item)) {
    return 'revealable';
  }

  return 'visible';
}

type PartitionedFieldErrors = {
  blocking: AntdFieldError[];
  inapplicable: AntdFieldError[];
  revealable: AntdFieldError[];
};

export function partitionFieldErrorsByVisibility(
  form: ClassifyForm,
  fieldErrors: AntdFieldError[],
  options: { alwaysBlock?: (field: AntdFieldError) => boolean } = {},
): PartitionedFieldErrors {
  const blocking: AntdFieldError[] = [];
  const inapplicable: AntdFieldError[] = [];
  const revealable: AntdFieldError[] = [];

  for (const field of fieldErrors) {
    const visibility = classifyFieldErrorVisibility(form, field.name);
    if (visibility === 'inapplicable' && !options.alwaysBlock?.(field)) {
      inapplicable.push(field);
    } else if (visibility === 'revealable') {
      revealable.push(field);
      blocking.push(field);
    } else {
      blocking.push(field);
    }
  }

  return { blocking, inapplicable, revealable };
}

function scrollToVisibleValidationError(
  form: ClassifyForm,
  firstField: AntdFieldError,
  formName?: string,
): void {
  requestAnimationFrame(() => {
    if (typeof form.getFieldInstance === 'function') {
      const instance = form.getFieldInstance(firstField.name);
      const item = findFormItemElement(instance);
      if (item && isInInactiveTabPane(item)) {
        revealTabForFormItem(item);
      }
    }

    form.scrollToField?.(firstField.name, {
      behavior: 'smooth',
      block: 'center',
    });

    const formRoot = formName
      ? document.querySelector(antdFormRootSelector(formName))
      : null;
    const root = formRoot ?? document;
    const errorEls = root.querySelectorAll('.ant-form-item-has-error');
    for (const el of errorEls) {
      if (!(el instanceof HTMLElement)) {
        continue;
      }
      if (isFormItemHidden(el)) {
        continue;
      }
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      break;
    }
  });
}

/**
 * Apply Zod field errors to antd, dropping errors on unmounted/hidden items.
 * Returns blocking errors only — empty means submit may proceed for the UI.
 */
export function applyVisibleFormFieldErrors(
  form: ClassifyForm & {
    setFields: (fields: AntdFieldError[]) => void;
  },
  fieldErrors: AntdFieldError[],
  options: {
    formName?: string;
    clearNames?: Array<string | (string | number)[]>;
    alwaysBlock?: (field: AntdFieldError) => boolean;
  } = {},
): { blocking: AntdFieldError[]; firstMessage: string | null } {
  const { blocking, inapplicable, revealable } =
    partitionFieldErrorsByVisibility(form, fieldErrors, {
      alwaysBlock: options.alwaysBlock,
    });

  const clearOps: AntdFieldError[] = [];
  if (options.clearNames?.length) {
    for (const name of options.clearNames) {
      clearOps.push({ name, errors: [] });
    }
  }
  for (const field of inapplicable) {
    clearOps.push({ name: field.name, errors: [] });
  }

  const setOps = [...clearOps, ...blocking];
  if (setOps.length > 0) {
    form.setFields(setOps);
  }

  if (blocking.length === 0) {
    return { blocking: [], firstMessage: null };
  }

  const firstField =
    revealable[0] ??
    blocking.find((field) => field.name !== '_form') ??
    blocking[0];

  scrollToVisibleValidationError(form, firstField, options.formName);

  const firstMessage = firstField.errors[0] ?? blocking[0]?.errors[0] ?? null;

  return { blocking, firstMessage };
}

export function isGenericZodLocaleMessage(message: string): boolean {
  return (
    /^Invalid value\b/i.test(message) ||
    /^Invalid input\b/i.test(message) ||
    /^Invalid option\b/i.test(message) ||
    /^Too (big|small)\b/i.test(message) ||
    /^Unrecognized key/i.test(message)
  );
}

/** Prefer a translated fallback over Zod English locale defaults for toasts. */
export function resolveValidationToastMessage(
  firstMessage: string | null | undefined,
  fallbackMessage: string,
): string {
  if (firstMessage == null || firstMessage === '') {
    return fallbackMessage;
  }
  if (isGenericZodLocaleMessage(firstMessage)) {
    return fallbackMessage;
  }
  return firstMessage;
}

export function hasVisibleAntFormItemError(formName?: string): boolean {
  const formRoot = formName
    ? document.querySelector(antdFormRootSelector(formName))
    : null;
  const root = formRoot ?? document;
  const errorItems = root.querySelectorAll('.ant-form-item-has-error');
  for (const el of errorItems) {
    if (!(el instanceof HTMLElement)) {
      continue;
    }
    if (el.classList.contains('ant-form-item-hidden')) {
      continue;
    }
    const style = window.getComputedStyle?.(el);
    if (style && (style.display === 'none' || style.visibility === 'hidden')) {
      continue;
    }
    return true;
  }
  return false;
}
